import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { fetchContext, nextStep, StaffContext } from "../lib/staffContext";
import { AuthFrame } from "../ui/AuthFrame";
import { ContextError } from "../ui/ContextError";
import { useI18n } from "../i18n";
import { errorText, rpc } from "./admin/api";

const MIN_LENGTH = 12;
type FieldError = "short" | "mismatch" | "same" | "weak";

// First sign-in of a Sandbox test account (0011, F1). Zencera generated the
// password it signed in with, so that password opens nothing: no role works
// until the holder chooses their own. The database checks that the stored
// password really changed (confirm_password_set) — a Zencera person can't
// skip this screen by calling the function directly.
export function SetPassword() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [ctx, setCtx] = useState<StaffContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<FieldError | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // The new password is stored in Auth before the database is told; if only
  // the second step failed, retrying must not change the password again.
  const [authUpdated, setAuthUpdated] = useState(false);
  const newField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchContext().then((res) => {
      if (res.kind === "signed_out")
        navigate("/teacher/login", { replace: true });
      else if (res.kind === "no_access")
        navigate("/teacher/login?reason=no_access", { replace: true });
      else if (res.kind === "unavailable") setError(res.message);
      else if (!res.ctx.must_set_password)
        navigate(nextStep(res.ctx), { replace: true });
      else setCtx(res.ctx);
    });
  }, [navigate]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    if (!authUpdated) {
      if (pw.length < MIN_LENGTH) return setFieldError("short");
      if (pw !== pw2) return setFieldError("mismatch");
    }
    setFieldError(null);
    setBusy(true);
    if (!authUpdated) {
      const { error: authErr } = await supabase.auth.updateUser({
        password: pw,
      });
      if (authErr) {
        setBusy(false);
        const code = (authErr as { code?: string }).code;
        if (code === "same_password") return setFieldError("same");
        if (code === "weak_password") return setFieldError("weak");
        return setSaveError(t["pw.failed"]);
      }
      setAuthUpdated(true);
      setPw("");
      setPw2("");
    }
    const r = await rpc<StaffContext>("confirm_password_set");
    setBusy(false);
    if (r.error) return setSaveError(errorText(t, r.error));
    navigate(nextStep(r.data), { replace: true });
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate("/teacher/login");
  }

  if (error) return <ContextError message={error} />;
  if (!ctx)
    return (
      <main className="page-loading" role="status">
        {t.loading}
      </main>
    );

  const describedBy = ["pw-hint", fieldError ? "pw-error" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <AuthFrame
      title={t["pw.title"]}
      subtitle={t["pw.intro"](ctx.display_name)}
      focusField={newField}
    >
      <form className="form" onSubmit={submit} noValidate>
        {authUpdated ? (
          <p className="notice" role="status">
            {t["pw.savedRetry"]}
          </p>
        ) : (
          <>
            <label className="field-label" htmlFor="pw-new">
              {t["pw.new"]}
            </label>
            <input
              ref={newField}
              id="pw-new"
              className="field field-lg"
              type="password"
              autoComplete="new-password"
              minLength={MIN_LENGTH}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              aria-invalid={
                fieldError && fieldError !== "mismatch" ? true : undefined
              }
              aria-describedby={describedBy}
              required
            />
            <p className="form-hint" id="pw-hint">
              {t["pw.hint"](MIN_LENGTH)}
            </p>
            <label className="field-label" htmlFor="pw-again">
              {t["pw.again"]}
            </label>
            <input
              id="pw-again"
              className="field field-lg"
              type="password"
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              aria-invalid={fieldError === "mismatch" ? true : undefined}
              aria-describedby={
                fieldError === "mismatch" ? "pw-error" : undefined
              }
              required
            />
            {fieldError && (
              <p className="error-text" id="pw-error" role="alert">
                {fieldError === "short"
                  ? t["pw.tooShort"](MIN_LENGTH)
                  : fieldError === "mismatch"
                    ? t["pw.mismatch"]
                    : fieldError === "same"
                      ? t["pw.sameAsBefore"]
                      : t["pw.weak"]}
              </p>
            )}
          </>
        )}
        {saveError && (
          <p className="error-text" role="alert">
            {saveError}
          </p>
        )}
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={busy}
        >
          {busy ? t["pw.saving"] : authUpdated ? t["pw.retry"] : t["pw.save"]}
        </button>
      </form>
      <div className="form-actions">
        <button type="button" className="link-btn" onClick={signOut}>
          {t.signOut}
        </button>
      </div>
    </AuthFrame>
  );
}
